import { c as _c } from "react/compiler-runtime";
import { Modal, ModalSizeEnum, PrimaryButton, SecondaryButton, toast } from '@systemeio/ui';
import { ConfirmationEmailInterface } from 'modules/digital-product/types/confirmation-email-interface';
import { DigitalProductChangeInterface, DigitalProductErrorInterface } from 'modules/digital-product/types/digital-product-interface';
import { useStore } from 'modules/stores/store/hooks/use-store';
import { useStoreQuery } from 'modules/stores/store/hooks/use-store-query';
import React, { FC, useEffect, useState } from 'react';
import { useSetDefaultEmail } from 'shared/components/dashboard-mail-editor/hooks/use-set-default-email';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import { twMerge } from 'tailwind-merge';
import { ConfirmationEmailEditor, FILE_LINKS_SUBSTITUTE, PRODUCT_NAME_SUBSTITUTE } from './confirmation-email-editor';
type Props = {
  confirmationEmail: DigitalProductChangeInterface['confirmationEmail'];
  onChange: (data: DigitalProductChangeInterface['confirmationEmail']) => void;
  errors: DigitalProductErrorInterface['confirmationEmail'];
};
export const ConfirmationEmail: FC<Props> = t0 => {
  const $ = _c(61);
  const {
    confirmationEmail,
    onChange,
    errors
  } = t0;
  const {
    t
  } = useLocoTranslation();
  const {
    storeId
  } = useStoreQuery();
  const {
    data: storeView
  } = useStore(storeId);
  let t1;
  if ($[0] !== t) {
    t1 = t("dashboard.store.product.download.confirmation_email.default_subject");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = storeView?.locale;
  let t3;
  if ($[2] !== t || $[3] !== t2) {
    t3 = t("dashboard.store.product.download.confirmation_email.default_body", {
      product_name: `{${PRODUCT_NAME_SUBSTITUTE}}`,
      downloadable_file_links: `{${FILE_LINKS_SUBSTITUTE}}`,
      lng: t2
    });
    $[2] = t;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t1 || $[6] !== t3) {
    t4 = {
      subject: t1,
      body: t3,
      fromName: "",
      fromEmail: "",
      previewText: ""
    };
    $[5] = t1;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const [defaultConfirmationEmail, setDefaultConfirmationEmail] = useState(t4);
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = data => setDefaultConfirmationEmail(data);
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  useSetDefaultEmail(defaultConfirmationEmail, t5);
  const [opened, setOpened] = useState(false);
  const [tempState, setTempState] = useState(confirmationEmail ?? defaultConfirmationEmail);
  let t6;
  let t7;
  if ($[9] !== confirmationEmail) {
    t6 = () => {
      if (confirmationEmail) {
        setTempState(confirmationEmail);
      }
    };
    t7 = [confirmationEmail];
    $[9] = confirmationEmail;
    $[10] = t6;
    $[11] = t7;
  } else {
    t6 = $[10];
    t7 = $[11];
  }
  useEffect(t6, t7);
  const canSave = !!tempState.fromEmail && !!tempState.fromName && !!tempState.body && !!tempState.subject;
  let t8;
  if ($[12] !== onChange || $[13] !== t || $[14] !== tempState) {
    t8 = e => {
      e.preventDefault();
      if (!tempState.body.includes(`{${FILE_LINKS_SUBSTITUTE}}`)) {
        toast.error(t("dashboard.store.product.download.confirmation_email.require_fields"));
      } else {
        onChange(tempState);
        setOpened(false);
      }
    };
    $[12] = onChange;
    $[13] = t;
    $[14] = tempState;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const onSave = t8;
  const hasConfirmationEmailError = !!errors.subject || !!errors.body || !!errors.fromName || !!errors.fromEmail;
  const t9 = hasConfirmationEmailError && "bg-danger hover:bg-danger";
  let t10;
  if ($[16] !== t9) {
    t10 = twMerge("w-fit", t9);
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => setOpened(true);
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== t) {
    t12 = t("dashboard.store.product.download.confirmation_email.edit");
    $[19] = t;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== t10 || $[22] !== t12) {
    t13 = <PrimaryButton className={t10} onClick={t11}>{t12}</PrimaryButton>;
    $[21] = t10;
    $[22] = t12;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => setOpened(false);
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] !== t) {
    t15 = t("dashboard.store.product.download.confirmation_email.title");
    $[25] = t;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] !== confirmationEmail || $[28] !== defaultConfirmationEmail) {
    t16 = () => setTempState(confirmationEmail ?? defaultConfirmationEmail);
    $[27] = confirmationEmail;
    $[28] = defaultConfirmationEmail;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = [];
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  let t18;
  if ($[31] !== errors) {
    t18 = {
      ...errors,
      common: t17
    };
    $[31] = errors;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  let t19;
  if ($[33] !== t18 || $[34] !== tempState) {
    t19 = <ConfirmationEmailEditor tempState={tempState} onChange={setTempState} errors={t18} />;
    $[33] = t18;
    $[34] = tempState;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  let t20;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = e_0 => {
      e_0.preventDefault();
      setOpened(false);
    };
    $[36] = t20;
  } else {
    t20 = $[36];
  }
  let t21;
  if ($[37] !== t) {
    t21 = t("confirmation_modal.cancel");
    $[37] = t;
    $[38] = t21;
  } else {
    t21 = $[38];
  }
  let t22;
  if ($[39] !== t21) {
    t22 = <SecondaryButton width="large" onClick={t20} type="submit">{t21}</SecondaryButton>;
    $[39] = t21;
    $[40] = t22;
  } else {
    t22 = $[40];
  }
  const t23 = !canSave;
  let t24;
  if ($[41] !== t) {
    t24 = t("global.save");
    $[41] = t;
    $[42] = t24;
  } else {
    t24 = $[42];
  }
  let t25;
  if ($[43] !== onSave || $[44] !== t23 || $[45] !== t24) {
    t25 = <PrimaryButton disabled={t23} width="large" onClick={onSave}>{t24}</PrimaryButton>;
    $[43] = onSave;
    $[44] = t23;
    $[45] = t24;
    $[46] = t25;
  } else {
    t25 = $[46];
  }
  let t26;
  if ($[47] !== t22 || $[48] !== t25) {
    t26 = <div className="flex justify-center gap-4">{t22}{t25}</div>;
    $[47] = t22;
    $[48] = t25;
    $[49] = t26;
  } else {
    t26 = $[49];
  }
  let t27;
  if ($[50] !== t19 || $[51] !== t26) {
    t27 = <form className="flex flex-col gap-6 lg:gap-7">{t19}{t26}</form>;
    $[50] = t19;
    $[51] = t26;
    $[52] = t27;
  } else {
    t27 = $[52];
  }
  let t28;
  if ($[53] !== opened || $[54] !== t15 || $[55] !== t16 || $[56] !== t27) {
    t28 = <Modal opened={opened} size={ModalSizeEnum.big} onClose={t14} title={t15} afterLeave={t16}>{t27}</Modal>;
    $[53] = opened;
    $[54] = t15;
    $[55] = t16;
    $[56] = t27;
    $[57] = t28;
  } else {
    t28 = $[57];
  }
  let t29;
  if ($[58] !== t13 || $[59] !== t28) {
    t29 = <>{t13}{t28}</>;
    $[58] = t13;
    $[59] = t28;
    $[60] = t29;
  } else {
    t29 = $[60];
  }
  return t29;
};